"use client"

import * as React from "react"
import { Check, ChevronsUpDown, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

export interface MultiSelectOption {
  value: string
  label: string
  shortLabel?: string  // Краткое название (kls_names)
  fullLabel?: string   // Полное название для tooltip (kls_namef)
  indentLevel?: number // Уровень вложенности для иерархии
  disabled?: boolean
}

interface MultiSelectComboboxProps {
  options: MultiSelectOption[]
  values: string[]
  onValuesChange: (values: string[]) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  className?: string
  disabled?: boolean
  maxDisplayedItems?: number  // Максимальное количество отображаемых бейджей
  allItemsLabel?: string      // Текст для "все элементы"
}

export function MultiSelectCombobox({
  options,
  values,
  onValuesChange,
  placeholder = "Выберите значения...",
  searchPlaceholder = "Поиск...",
  emptyText = "Ничего не найдено",
  className,
  disabled = false,
  maxDisplayedItems = 2,
  allItemsLabel = "Все",
}: MultiSelectComboboxProps) {
  const [open, setOpen] = React.useState(false)
  const [searchValue, setSearchValue] = React.useState("")

  // Выбранные опции
  const selectedOptions = options.filter((option) => values.includes(option.value))

  // Фильтрация опций по поиску (поиск по label, shortLabel, fullLabel)
  const filteredOptions = React.useMemo(() => {
    if (!searchValue.trim()) return options
    
    const search = searchValue.toLowerCase()
    return options.filter((option) => {
      const matchLabel = option.label.toLowerCase().includes(search)
      const matchShort = option.shortLabel?.toLowerCase().includes(search)
      const matchFull = option.fullLabel?.toLowerCase().includes(search)
      return matchLabel || matchShort || matchFull
    })
  }, [options, searchValue])

  // Переключение выбора опции
  const toggleOption = (optionValue: string) => {
    if (values.includes(optionValue)) {
      onValuesChange(values.filter((v) => v !== optionValue))
    } else {
      onValuesChange([...values, optionValue])
    }
  }

  // Удаление одного значения (нельзя <button> внутри триггера Popover — там уже Button)
  const removeValue = (valueToRemove: string, e: React.SyntheticEvent) => {
    e.stopPropagation()
    e.preventDefault()
    onValuesChange(values.filter((v) => v !== valueToRemove))
  }

  const clearAll = (e: React.SyntheticEvent) => {
    e.stopPropagation()
    e.preventDefault()
    onValuesChange([])
  }

  const removeKeyDown = (valueToRemove: string, e: React.KeyboardEvent) => {
    if (e.key !== "Enter" && e.key !== " ") return
    e.stopPropagation()
    e.preventDefault()
    onValuesChange(values.filter((v) => v !== valueToRemove))
  }

  const clearAllKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "Enter" && e.key !== " ") return
    e.stopPropagation()
    e.preventDefault()
    onValuesChange([])
  }

  // Выбрать все (из текущего поиска)
  const selectAllFiltered = () => {
    const filteredValues = filteredOptions.map((o) => o.value)
    const newValues = [...new Set([...values, ...filteredValues])]
    onValuesChange(newValues)
  }

  // Отображение выбранных значений
  const renderSelectedValues = () => {
    if (values.length === 0) {
      return <span className="text-muted-foreground">{placeholder}</span>
    }

    // Если выбраны все - показываем allItemsLabel
    if (values.length === options.length && options.length > 0) {
      return <span>{allItemsLabel}</span>
    }

    // Если выбрано мало - показываем бейджи
    if (selectedOptions.length <= maxDisplayedItems) {
      return (
        <div className="flex flex-wrap gap-1">
          {selectedOptions.map((option) => (
            <Badge
              key={option.value}
              variant="secondary"
              className="text-xs px-1.5 py-0"
              title={option.fullLabel || option.label}
            >
              {option.shortLabel || option.label}
              <span
                role="button"
                tabIndex={0}
                className="ml-1 inline-flex cursor-pointer rounded-sm hover:text-destructive focus:outline-none focus:ring-1 focus:ring-ring"
                onClick={(e) => removeValue(option.value, e)}
                onKeyDown={(e) => removeKeyDown(option.value, e)}
                aria-label={`Удалить «${option.shortLabel || option.label}»`}
              >
                <X className="h-3 w-3" />
              </span>
            </Badge>
          ))}
        </div>
      )
    }

    // Если много выбрано - показываем счётчик
    return (
      <div className="flex items-center gap-1">
        <Badge variant="secondary" className="text-xs">
          {selectedOptions.length} выбрано
        </Badge>
        <span
          role="button"
          tabIndex={0}
          className="ml-1 inline-flex cursor-pointer rounded-sm hover:text-destructive focus:outline-none focus:ring-1 focus:ring-ring"
          onClick={clearAll}
          onKeyDown={clearAllKeyDown}
          title="Очистить всё"
          aria-label="Очистить все выбранные"
        >
          <X className="h-4 w-4" />
        </span>
      </div>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between min-h-[36px] h-auto", className)}
          disabled={disabled}
        >
          <div className="flex-1 flex items-center overflow-hidden text-left">
            {renderSelectedValues()}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 z-[9999]" align="start">
        <Command shouldFilter={false}>
          <CommandInput 
            placeholder={searchPlaceholder}
            value={searchValue}
            onValueChange={setSearchValue}
          />
          <CommandList className="max-h-[300px]">
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {/* Быстрые действия */}
              <div className="flex items-center gap-2 px-2 py-1.5 border-b mb-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={selectAllFiltered}
                  disabled={filteredOptions.length === 0}
                >
                  Выбрать все
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={clearAll}
                  disabled={values.length === 0}
                >
                  Очистить
                </Button>
                {values.length > 0 && (
                  <span className="text-xs text-muted-foreground ml-auto">
                    {values.length} из {options.length}
                  </span>
                )}
              </div>
              
              {/* Список опций */}
              {filteredOptions.map((option) => {
                const isSelected = values.includes(option.value)
                const paddingLeft = option.indentLevel ? option.indentLevel * 16 : 0
                const prefix = option.indentLevel && option.indentLevel > 0 ? '└─ ' : ''
                const displayLabel = option.shortLabel || option.label
                
                return (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                    onSelect={() => toggleOption(option.value)}
                    style={{ paddingLeft: `${paddingLeft + 8}px` }}
                    title={option.fullLabel || option.label}
                    className="cursor-pointer"
                  >
                    <div className={cn(
                      "mr-2 h-4 w-4 border rounded flex items-center justify-center",
                      isSelected ? "bg-primary border-primary" : "border-input"
                    )}>
                      {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                    </div>
                    <span className="truncate" title={option.fullLabel || option.label}>
                      {prefix}{displayLabel}
                    </span>
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
